import { useCallback, useEffect, useState } from "react";
import { Constants } from "../constants/constants";
import { getUsersAsync } from "../api/UserApi";
import { User } from "../models/User";
import { UserListResult } from "../models/UserModels";

const DEFAULT_PAGE_SIZE = 10;

interface UseUsersReturn {
  users: User[];
  pagination: {
    page: number;
    pageSize: number;
    totalCount: number;
    totalPages: number;
    hasNext: boolean;
    hasPrevious: boolean;
  };
  loading: boolean;
  error: string | null;
  setPage: (page: number) => void;
  reload: () => Promise<void>;
}

export const useUsers = (
  organizationId?: string,
  pageSize: number = DEFAULT_PAGE_SIZE,
): UseUsersReturn => {
  const [page, setPage] = useState(1);
  const [data, setData] = useState<UserListResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const token = localStorage.getItem(Constants.ACCESS_TOKEN) || "";

  const load = useCallback(async () => {
    if (!token) {
      setError("Unauthorized");
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const res = await getUsersAsync(token, page, pageSize, organizationId);
      if (res.success && res.result) {
        setData(res.result);
        setError(null);
      } else {
        setError(res.message || "Failed to load users");
      }
    } catch (err) {
      console.error(err);
      setError("Failed to load users");
    } finally {
      setLoading(false);
    }
  }, [token, page, pageSize, organizationId]);

  useEffect(() => {
    load();
  }, [load]);

  // Reset to page 1 whenever the selected organization changes
  useEffect(() => {
    setPage(1);
  }, [organizationId]);

  const reload = useCallback(async () => {
    setPage(1);
    await load();
  }, [load]);

  return {
    users: data?.items ?? [],
    pagination: {
      page: data?.page ?? page,
      pageSize: data?.pageSize ?? pageSize,
      totalCount: data?.totalCount ?? 0,
      totalPages: data?.totalPages ?? 1,
      hasNext: data?.hasNext ?? false,
      hasPrevious: data?.hasPrevious ?? false,
    },
    loading,
    error,
    setPage,
    reload,
  };
};